import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type ThemePreference = "light" | "dark" | "system";

interface ThemeState {
  preference: ThemePreference;
  isDark: boolean;
  setPreference: (value: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);
const STORAGE_KEY = "zepadoca-manager:theme";

function resolveSystem(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    // O acesso pode lançar em janela anônima ou com dados de site bloqueados.
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "light" || saved === "dark" || saved === "system") return saved;
    } catch {
      // Sem preferência guardada, o sistema decide. Nada quebra.
    }
    return "system";
  });

  const [isDark, setIsDark] = useState(() =>
    preference === "system" ? resolveSystem() : preference === "dark",
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    function apply() {
      const dark = preference === "system" ? media.matches : preference === "dark";
      setIsDark(dark);
      document.documentElement.classList.toggle("dark", dark);
    }

    apply();
    // Só acompanha a preferência do sistema quando é ela que manda: com uma
    // escolha explícita, mudar o tema do sistema não pode trocar o do painel.
    if (preference !== "system") return;
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [preference]);

  function setPreference(value: ThemePreference) {
    setPreferenceState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Preferência não persistida é aceitável; o tema da sessão já mudou.
    }
  }

  return (
    <ThemeContext.Provider value={{ preference, isDark, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme precisa estar dentro de ThemeProvider");
  return ctx;
}
